import { type InfiniteData, useQuery } from '@tanstack/react-query';
import { isLoggedInAtom } from 'app/atoms';
import { http } from 'app/lib/axios';
import { useInfiniteQueryLPC } from 'app/lib/query';
import { monthlyStatsKeys } from 'app/network/keys';
import type { FoundUser } from 'app/network/user';
import type { FunctionalUseInfiniteQueryOptions, FunctionalUseQueryOptions, Pagination } from 'app/types/network';
import { useAtomValue } from 'jotai';

export interface UserMonthlyStatsResponseDTO {
  period: string;
  numUsersTarget: number;
  numLoans: number;
  numMyLoans: number;
  totalEstimatedPrice: number;
  targetMultiplier: number;
  isFreeze: boolean;
}

export interface MonthlyStreakMember extends FoundUser {
  hasContributed: boolean;
}

/**
 * GET /users/monthly-stats/all
 * Returns the current month's live stats + the last 11 months, most-recent first.
 */
export function useMonthlyStatsAll(opts: FunctionalUseQueryOptions<UserMonthlyStatsResponseDTO[]> = {}) {
  const isLoggedIn = useAtomValue(isLoggedInAtom);
  return useQuery<UserMonthlyStatsResponseDTO[]>({
    queryKey: monthlyStatsKeys.list(),
    queryFn: () => http.get<UserMonthlyStatsResponseDTO[]>('/users/monthly-stats/all').then((res) => res.data),
    ...opts,
    enabled: isLoggedIn && (opts.enabled ?? true),
  });
}

/**
 * GET /users/monthly-stats/:period
 * Returns live (freshly computed) stats for a single month. period format: YYYY-MM.
 */
export function useMonthlyStats(period: string, opts: FunctionalUseQueryOptions<UserMonthlyStatsResponseDTO> = {}) {
  const isLoggedIn = useAtomValue(isLoggedInAtom);
  return useQuery<UserMonthlyStatsResponseDTO>({
    queryKey: monthlyStatsKeys.detail(period),
    queryFn: () => http.get<UserMonthlyStatsResponseDTO>(`/users/monthly-stats/${period}`).then((res) => res.data),
    ...opts,
    enabled: isLoggedIn && !!period && (opts.enabled ?? true),
  });
}

export function useMonthlyStreakMembers(
  period: string,
  take = 25,
  opts: FunctionalUseInfiniteQueryOptions<
    Pagination<MonthlyStreakMember>,
    Error,
    InfiniteData<Pagination<MonthlyStreakMember>, number>,
    readonly unknown[],
    number
  > = {},
) {
  const isLoggedIn = useAtomValue(isLoggedInAtom);
  return useInfiniteQueryLPC<
    Pagination<MonthlyStreakMember>,
    Error,
    InfiniteData<Pagination<MonthlyStreakMember>, number>,
    readonly unknown[],
    number
  >({
    queryKey: monthlyStatsKeys.members(period, take),
    queryFn: ({ pageParam }) =>
      http
        .get<Pagination<MonthlyStreakMember>>('/users/monthly-stats/members', {
          params: { period, take, skip: pageParam },
        })
        .then((res) => res.data),
    ...opts,
    enabled: isLoggedIn && !!period && (opts.enabled ?? true),
    initialPageParam: 0,
    getNextPageParam: (previousPage) =>
      previousPage.skip + previousPage.take < previousPage.total ? previousPage.skip + previousPage.take : undefined,
  });
}
